import { logger } from '@darkchat/sdk/utils/logger'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { SiGoogle, SiDiscord, SiGithub } from 'react-icons/si'
import { Logo } from '../components/Logo'
import type { OAuthProvider, OAuthResult } from '@darkchat/types'
import { LoadingScreen } from '../components/LoadingScreen'

// SDK core functions
import {
  createAnonymousSoul,
  retrieveSoul,
  retrieveSoulByOAuth,
  engraveSoul,
  RequestError
} from '@darkchat/sdk'

// SDK zustand stores
import {
  useLanguageStore,
  useSessionStore,
  useSoulSelfStore,
} from '@darkchat/sdk'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()

  // Zustand stores
  const { language, setLanguage, initializeLanguage } = useLanguageStore()
  const {
    soulId,
    sessionToken,
    setSoulId,
    setSessionToken,
    clearSession,
    isSessionValid
  } = useSessionStore()
  const {
    setSoulSelf,
  } = useSoulSelfStore()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isRestoringSession, setIsRestoringSession] = useState(true)
  const [isSessionRestored, setIsSessionRestored] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [isTurnstileLoaded, setIsTurnstileLoaded] = useState(false)
  const turnstileRef = useRef<HTMLDivElement>(null)

  // セッション復元
  useEffect(() => {
    initializeLanguage()

    // URLからroomIdを抽出して保存
    const pathname = location.pathname
    const roomIdMatch = pathname.match(/\/abyss\/([a-zA-Z0-9-]+)/)
    if (roomIdMatch) {
      const roomId = roomIdMatch[1]!
      sessionStorage.setItem('pendingRoomId', roomId)
      logger.log('[Frontend] Saved pending roomId:', roomId)
    }

    const restoreSession = async () => {
      // Zustand persistで自動復元されたセッションを確認
      if (isSessionValid() && soulId && sessionToken) {
        try {
          setIsRestoringSession(true)
          const soulSelf = await retrieveSoul(soulId, sessionToken)
          setSoulSelf(soulSelf)
          setIsSessionRestored(true)
          logger.log('[Frontend] Session restored')

          // 保存されたroomIdがあればそこに遷移、なければ通常のabyss
          const pendingRoomId = sessionStorage.getItem('pendingRoomId')
          if (pendingRoomId) {
            sessionStorage.removeItem('pendingRoomId')
            navigate(`/${language}/abyss/${pendingRoomId}`)
          } else {
            navigate(`/${language}/abyss`)
          }
        } catch (err) {
          logger.error('Session restoration failed:', err)
          // セッション無効なのでクリア
          clearSession()
        } finally {
          setIsRestoringSession(false)
        }
      } else {
        setIsRestoringSession(false)
      }
    }

    restoreSession()
  }, []) // 初回マウント時のみ実行

  // Turnstileウィジェット初期化
  useEffect(() => {
    // Turnstileが利用可能でSite Keyが設定されている場合のみ初期化
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY

    if (!siteKey || siteKey === 'your_turnstile_site_key_here') {
      // Site Keyが未設定またはプレースホルダーの場合はスキップ
      logger.log('Turnstile Site Key not configured, skipping widget initialization')
      return
    }

    // Turnstileスクリプトがロードされるのを待つ
    const checkTurnstile = setInterval(() => {
      if (window.turnstile && turnstileRef.current && !isTurnstileLoaded) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: siteKey,
          theme: 'dark',
          size: 'normal',
          callback: (token: string) => {
            logger.log('Turnstile token received')
            setTurnstileToken(token)
          },
          'error-callback': () => {
            logger.error('Turnstile verification failed')
            setTurnstileToken(null)
          },
          'expired-callback': () => {
            logger.log('Turnstile token expired')
            setTurnstileToken(null)
          }
        })
        setIsTurnstileLoaded(true)
        clearInterval(checkTurnstile)
      }
    }, 100)

    // 5秒後にタイムアウト
    const timeout = setTimeout(() => {
      clearInterval(checkTurnstile)
      if (!isTurnstileLoaded) {
        logger.warn('Turnstile widget failed to load')
      }
    }, 5000)

    return () => {
      clearInterval(checkTurnstile)
      clearTimeout(timeout)
    }
  }, [isRestoringSession, isTurnstileLoaded])

  // 匿名参加
  const handleAnonymousLogin = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Turnstileトークンの確認
      if (!turnstileToken && isTurnstileLoaded) {
        setError(language === 'ja'
          ? '門番の試練を通過せよ'
          : 'The gatekeeper demandeth thy proof'
        )
        setIsLoading(false)
        return
      }

      // 匿名ソウル作成
      const { soul, sessionToken: newToken } = await createAnonymousSoul(
        language,
        turnstileToken || undefined
      )

      // Zustand storeに保存（自動的にlocalStorageに永続化される）
      setSoulId(soul.id)
      setSessionToken(newToken)

      // チャット画面へ遷移（保存されたroomIdがあればそこへ）
      const pendingRoomId = sessionStorage.getItem('pendingRoomId')
      if (pendingRoomId) {
        sessionStorage.removeItem('pendingRoomId')
        navigate(`/${language}/abyss/${pendingRoomId}`)
      } else {
        navigate(`/${language}/abyss`)
      }
    } catch (err) {
      logger.error('Anonymous login failed:', err)

      if (err instanceof RequestError) {
        // API エラーの場合
        setError(language === 'ja'
          ? `深淵からの拒絶: ${err.message}`
          : `The void rejecteth thee: ${err.message}`
        )
      } else {
        // その他のエラー
        setError(language === 'ja'
          ? '名無き者の道は閉ざされた。再び試みよ。'
          : 'The nameless path denieth thee. Attempt once more.'
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  // OAuthログイン
  const handleOAuthLogin = async (provider: OAuthProvider) => {
    setIsLoading(true)
    setError(null)

    try {
      // Turnstileトークンの確認（匿名ログインと同じ）
      if (!turnstileToken && isTurnstileLoaded) {
        setError(language === 'ja'
          ? '門番の試練を通過せよ'
          : 'The gatekeeper demandeth thy proof'
        )
        setIsLoading(false)
        return
      }

      // API URLを構築
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787/api/v1'
      const oauthRedirectUrl = `${apiUrl}/oauth/${provider}/redirect?language=${language}`

      // OAuthプロバイダーへリダイレクト
      window.location.href = oauthRedirectUrl
    } catch (err) {
      logger.error(`OAuth login failed for ${provider}:`, err)

      if (err instanceof RequestError) {
        setError(language === 'ja'
          ? `魂の認証に失敗: ${err.message}`
          : `Soul verification faileth: ${err.message}`
        )
      } else {
        setError(language === 'ja'
          ? '魂の契約が成立せず'
          : 'The soul pact hath failed'
        )
      }
      setIsLoading(false)
    }
  }

  const toggleLanguage = () => {
    const newLang = language === 'ja' ? 'en' : 'ja'
    setLanguage(newLang)
    setError(null)
  }

  const HelmetElement = () => {

    const seoData = {
      ja: {
        title: 'DarkChat (ダークチャット) - 深淵への扉 | Anti-UXチャットアプリ',
        description: 'DarkChatは意図的な不快感を通じて深い情報層へアクセスする革新的なAnti-UXチャットアプリです。深度システムにより、痛みと苦痛を代償に真実へ到達します。',
        keywords: 'DarkChat, ダークチャット, Anti-UX, チャットアプリ, 深淵, 奈落, 虚無, 深度システム, 痛み, 苦痛, 匿名チャット, リアルタイムチャット',
        ogTitle: 'DarkChat - 深淵への扉',
        ogDescription: '意図的な不快感を通じて深い情報層へアクセスする革新的なチャットアプリ'
      },
      en: {
        title: 'DarkChat - Threshold to the Nethermore Void | Anti-UX Chat Application',
        description: 'DarkChat be an innovative Anti-UX chat application that doth grant passage to deeper strata of knowledge through wilful torment. Forsake thy comfort for verity through our fathomless system.',
        keywords: 'DarkChat, Anti-UX, chat application, abyss, nethermore, void, depth system, pain, suffering, torment, anonymous chat',
        ogTitle: 'DarkChat - Threshold to the Nethermore Void',
        ogDescription: 'A revolutionary parlour that doth trade solace for passage to deeper realms of knowledge'
      }
    }

    const currentSeo = seoData[language]

    // Structured data for SEO (JSON-LD)
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'DarkChat',
      url: 'https://darkchat.app',
      description: currentSeo.description,
      applicationCategory: 'CommunicationApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      author: {
        '@type': 'Organization',
        name: 'DarkChat Team'
      },
      inLanguage: [language],
      availableLanguage: ['ja', 'en']
    }
    return (
      <Helmet>
        <html lang={language} />
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <meta name="keywords" content={currentSeo.keywords} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://darkchat.app/${language}`} />
        <meta property="og:title" content={currentSeo.ogTitle} />
        <meta property="og:description" content={currentSeo.ogDescription} />
        <meta property="og:image" content="https://darkchat.app/og-image.jpg" />
        <meta property="og:locale" content={language === 'ja' ? 'ja_JP' : 'en_US'} />
        <meta property="og:locale:alternate" content={language === 'ja' ? 'en_US' : 'ja_JP'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://darkchat.app/${language}`} />
        <meta name="twitter:title" content={currentSeo.ogTitle} />
        <meta name="twitter:description" content={currentSeo.ogDescription} />
        <meta name="twitter:image" content="https://darkchat.app/og-image.jpg" />

        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <link rel="canonical" href={`https://darkchat.app/${language}`} />
        <link rel="alternate" hrefLang="ja" href="https://darkchat.app/ja" />
        <link rel="alternate" hrefLang="en" href="https://darkchat.app/en" />
        <link rel="alternate" hrefLang="x-default" href="https://darkchat.app" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet> 
    );
  }

  // SEO meta tags
  

  // セッション復元中は何も表示しない
  if (isRestoringSession) {
    return (
      <LoadingScreen message={language === 'ja' ? '過去の契約を探索中...' : 'Seeking thy former bond...'} />
    )
  }
  
  return (
    <>
      <HelmetElement />
      <div className="h-full bg-[#0a0a0a] flex items-center justify-center p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Logo size="xl" />
          </div>
          <p className="text-gray-500 text-sm">
            {language === 'ja' ? '深淵への扉' : 'Threshold to the Nethermore Void'}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 p-4 bg-red-900/20 border border-red-900/50 rounded"
            >
              <p className="text-red-400 text-sm">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAnonymousLogin}
            disabled={isLoading || (isTurnstileLoaded && !turnstileToken)}
            className="w-full py-4 bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] hover:from-[#2a2a2a] hover:to-[#1a1a1a] border border-[#8B0000]/30 hover:border-[#8B0000]/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title={isTurnstileLoaded && !turnstileToken
              ? (language === 'ja' ? 'ゲートを準備中...' : 'Prithee, prove thou art no automaton')
              : undefined
            }
          >
            <span className="text-gray-400 font-medium">
              {isTurnstileLoaded && !turnstileToken
                ? (language === 'ja' ? 'ゲートを準備中...' : 'The ward awakens...')
                : (language === 'ja' ? '匿名で深淵へ' : 'Enter Namelessly')
              }
            </span>
            <div className="text-xs text-gray-600 mt-1">
              {isTurnstileLoaded && !turnstileToken
                ? (language === 'ja' ? '魔力を注いでいます' : 'The seal demandeth thy touch')
                : (language === 'ja' ? '身元を隠して参加する' : 'Venture forth without thy true visage')
              }
            </div>
          </motion.button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-800"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#0a0a0a] text-gray-600">
                {language === 'ja' ? 'または' : 'or perchance'}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleOAuthLogin('google')}
              disabled={isLoading || (isTurnstileLoaded && !turnstileToken)}
              className="w-full py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-gray-800 hover:border-gray-700 transition-all flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SiGoogle className="w-5 h-5 text-red-400" />
              <span className="text-gray-400">
                {language === 'ja' ? 'Googleで深淵へ' : 'Via Google grimoire'}
              </span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleOAuthLogin('discord')}
              disabled={isLoading || (isTurnstileLoaded && !turnstileToken)}
              className="w-full py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-gray-800 hover:border-gray-700 transition-all flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SiDiscord className="w-5 h-5 text-indigo-400" />
              <span className="text-gray-400">
                {language === 'ja' ? 'Discordで深淵へ' : 'Via Discord pact'}
              </span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleOAuthLogin('github')}
              disabled={isLoading || (isTurnstileLoaded && !turnstileToken)}
              className="w-full py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-gray-800 hover:border-gray-700 transition-all flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SiGithub className="w-5 h-5 text-white" />
              <span className="text-gray-400">
                {language === 'ja' ? 'GitHubで深淵へ' : 'Via GitHub codex'}
              </span>
            </motion.button>
          </div>
        </div>

        {/* Turnstileウィジェット（匿名ログインボタンの前に表示） */}
        {import.meta.env.VITE_TURNSTILE_SITE_KEY && import.meta.env.VITE_TURNSTILE_SITE_KEY !== 'your_turnstile_site_key_here' && (
          <div className="mt-6 mb-4 flex justify-center">
            <div ref={turnstileRef} className="cf-turnstile" />
          </div>
        )}

        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-4 text-sm">
            <button
              onClick={toggleLanguage}
              className="text-gray-600 hover:text-gray-400 transition-colors"
            >
              {language === 'ja' ? 'English' : '日本語'}
            </button>
            <span className="text-gray-700">|</span>
            <a
              href="/codex/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gray-400 transition-colors"
            >
              Codex
            </a>
          </div>
        </div>

        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <div className="text-center">
              <div className="w-12 h-12 border-2 border-[#8B0000] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500">
                {language === 'ja' ? '奈落への扉を開いています...' : 'The void beckons, drawing thee hence...'}
              </p>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
    </>
  )
}
